// Form builder review (milestone 17): pick a form and review its submissions,
// approving or rejecting the ones awaiting admin approval. Approved + verified
// submissions become active and flow to the form's source table on the next
// export.
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useState } from 'react'

import { api, can } from '../api'
import { ErrorBox, Pill } from '../components'
import { EnvToggle } from '../EnvToggle'
import { dt } from '../format'

const statuses = ['verified', 'pending', 'active', 'rejected', '']

export function Forms() {
  const me = useQuery({ queryKey: ['me'], queryFn: api.me })
  const admin = can(me.data, 'admin')
  const qc = useQueryClient()
  const formsQ = useQuery({ queryKey: ['forms'], queryFn: api.forms })
  const [sel, setSel] = useState('')
  const [status, setStatus] = useState('verified')
  const slug = sel || formsQ.data?.[0]?.slug || '' // default to the first form
  const form = formsQ.data?.find((f) => f.slug === slug)
  const refreshForms = () => qc.invalidateQueries({ queryKey: ['forms'] })

  const subsQ = useQuery({
    queryKey: ['form-submissions', slug, status],
    queryFn: () => api.formSubmissions(slug, status),
    enabled: !!slug,
  })
  const act = useMutation({
    mutationFn: ({ id, approve }: { id: number; approve: boolean }) => (approve ? api.approveSubmission(id) : api.rejectSubmission(id)),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['form-submissions'] }),
  })

  return (
    <section className="page">
      <header className="page-h">
        <div>
          <h1>Forms</h1>
          <p className="sub">Review form submissions. Approve the ones awaiting admin approval; verified + approved submissions become active and flow to the form's source table.</p>
        </div>
      </header>
      <div className="dash-filters">
        <label className="dash-filter">
          <span className="muted">Form</span>
          <select value={slug} onChange={(e) => setSel(e.target.value)}>
            {(formsQ.data ?? []).map((f) => (
              <option key={f.slug} value={f.slug}>{f.slug} ({f.status})</option>
            ))}
          </select>
        </label>
        <label className="dash-filter">
          <span className="muted">Status</span>
          <select value={status} onChange={(e) => setStatus(e.target.value)}>
            {statuses.map((s) => <option key={s} value={s}>{s || 'all'}</option>)}
          </select>
        </label>
      </div>
      {form && (
        <div className="env-row">
          <span className="muted">Publish</span>
          <EnvToggle
            label="Prod"
            version={form.published_version}
            current={form.current_version}
            canPublish
            admin={admin}
            publish={() => api.publishForm(form.slug)}
            unpublish={() => api.unpublishForm(form.slug)}
            onDone={refreshForms}
          />
          <EnvToggle
            label="Dev"
            version={form.dev_published_version}
            current={form.current_version}
            canPublish
            admin={admin}
            publish={() => api.publishFormDev(form.slug)}
            unpublish={() => api.unpublishFormDev(form.slug)}
            onDone={refreshForms}
          />
        </div>
      )}
      {formsQ.error && <ErrorBox error={formsQ.error} />}
      {subsQ.error && <ErrorBox error={subsQ.error} />}
      {act.error && <ErrorBox error={act.error} />}
      {formsQ.data?.length === 0 && <p className="muted">No forms yet.</p>}
      <table className="tbl">
        <thead>
          <tr>
            <th>Submitted</th>
            <th>Data</th>
            <th>Status</th>
            <th>Met</th>
            {admin && <th>Review</th>}
          </tr>
        </thead>
        <tbody>
          {(subsQ.data ?? []).map((s) => (
            <tr key={s.id}>
              <td className="muted">{dt(s.created_at)}</td>
              <td>
                {Object.entries(s.data).map(([k, v]) => (
                  <div key={k}><span className="muted mono">{k}:</span> {v}</div>
                ))}
              </td>
              <td><Pill kind={s.status === 'active' ? 'ok' : s.status === 'rejected' ? 'err' : 'neutral'}>{s.status}</Pill></td>
              <td className="mono muted">{s.requirements_met.join(', ')}</td>
              {admin && (
                <td>
                  {s.status !== 'rejected' && s.status !== 'active' && (
                    <button disabled={act.isPending} onClick={() => act.mutate({ id: s.id, approve: true })}>Approve</button>
                  )}
                  {s.status !== 'rejected' && (
                    <button disabled={act.isPending} onClick={() => act.mutate({ id: s.id, approve: false })}>Reject</button>
                  )}
                </td>
              )}
            </tr>
          ))}
        </tbody>
      </table>
      {slug && !subsQ.isLoading && subsQ.data?.length === 0 && <p className="muted">No submissions with this status.</p>}
    </section>
  )
}
